import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, Navigate, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Container, Nav, NavItem, NavLink, Row, TabContent, TabPane, Table, Spinner } from 'reactstrap';
import UiContent from '../../../../Components/Common/UiContent';
import WeatherForecast from './Widgets/WeatherForecast';
import WeatherForecastVariable from './Widgets/WeatherForecastVariable';
import Mortality from './Widgets/Mortality';
import RelativeGrowthIndex from './Widgets/RelativeGrowthIndex';
import MapWithLayers from './Widgets/MapWithLayers';
import FuelStorage from './Widgets/FuelStorage';
import Captaincy from './Widgets/Captaincy';
import UpcomingActivity from './Widgets/UpcomingActivities';
import Fuel from './Widgets/Fuel';
import DissolvedOxygenSummary from './Widgets/DissolvedOxygenSummary';
import OxygenSaturationSummary from './Widgets/OxygenSaturationSummary';
import TemperatureSummary from './Widgets/TemperatureSummary';
import useRedirectEstablishmentActiveContent from '../../../../Components/Hooks/useRedirectEstablishmentActiveContent';
import { getEstablishment } from '../../../../slices/thunks';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import OPlaceholder from '../../../../Components/OElements/OPlaceholder';
import UpcomingSchedules from './Widgets/UpcomingSchedules';
import EstablishmentInfo from './Widgets/EstablishmentInfo';
import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import Search404 from '../../../AuthenticationInner/Errors/Search';

function LoadingWrapper({ isLoading, children }) {
    return isLoading ? (
        <OPlaceholder />
    ) : (
        children
    );
}

const EstablishmentSummary = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const redirectEstablishmentActiveContent = useRedirectEstablishmentActiveContent('/establishment/summary/establishmentId');
    const [activeTab, setActiveTab] = useState("cp");

    const selectorEstablishment = createSelector(
        state => state.Establishments,
        result => ({
            establishment: result.itemEstablishmentDetail,
            isLoading: result.isLoadingList
        })
    );

    const { establishment, isLoading } = useSelector(selectorEstablishment);

    useEffect(() => {
        if (id && id != 'all') {
            dispatch(getEstablishment(id));
        }
    }, [id]);


    if (id === 'all')
        return (
            <React.Fragment>
                <UiContent />
                <div className="page-content mb-10" style={{ paddingBottom: '90px' }}>
                    <Container fluid>
                        <BreadCrumb title={`Dashboard `} hidden={true} />
                        <Search404 />
                    </Container>
                </div>
            </React.Fragment>
        )

    return (
        <React.Fragment>
            <UiContent />
            <div className="page-content mb-10" style={{ paddingBottom: '90px' }}>
                <Container fluid>
                    <BreadCrumb title={`Dashboard ${establishment?.name}`} hidden={true} />
                    <Row className="layout-dashboard" style={{ justifyContent: 'center' }}>
                        <Col md={2} className='summary-dashboard-1 '  >
                            <Row className='d-none d-md-block'>
                                <Col md={12}>
                                    <LoadingWrapper isLoading={!establishment}>
                                        <EstablishmentInfo id={establishment?._id} />
                                    </LoadingWrapper>
                                </Col>
                            </Row>
                            <Row className='d-none d-md-block'>
                                <Col md={12}>
                                    <LoadingWrapper isLoading={!establishment}>
                                        <MapWithLayers
                                            title={'Mapa'}
                                            code_siep={establishment?.code_siep}
                                            height={300}
                                            lat={establishment?.latitude}
                                            lon={establishment?.longitude}
                                            code={establishment?.establishment_type?.code}
                                        />
                                    </LoadingWrapper>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <LoadingWrapper isLoading={!establishment}>
                                        <WeatherForecast
                                            code_siep={establishment?.code_siep}
                                            lat={establishment?.latitude}
                                            lon={establishment?.longitude}
                                        />
                                    </LoadingWrapper>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} >
                                    <LoadingWrapper isLoading={!establishment}>
                                        <WeatherForecastVariable
                                            code_siep={establishment?.code_siep}
                                            lat={establishment?.latitude}
                                            lon={establishment?.longitude}
                                        />
                                    </LoadingWrapper>
                                </Col>
                            </Row>
                        </Col>

                        <Col md={8} className='summary-dashboard-2'>
                            <Row>
                                <Col md={12}>
                                    <LoadingWrapper isLoading={!establishment}>
                                        <UpcomingActivity />
                                    </LoadingWrapper>
                                </Col>
                            </Row>
                            <Row style={{ flex: 1 }}>
                                <Col md={12} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                                    <Row style={{ flex: 1, rowGap: '5px' }}>
                                        <Col md={8} className='element-2-md-8'>
                                            <LoadingWrapper isLoading={!establishment}>
                                                <RelativeGrowthIndex dataColors='["--vz-primary", "--vz-secondary", "--vz-success"]' />
                                            </LoadingWrapper>
                                        </Col>
                                        <Col md={4} className='element-2-md-4'>
                                            <LoadingWrapper isLoading={!establishment}>
                                                <OxygenSaturationSummary title={"Saturación Oxígeno"} um={"%"} code_siep={establishment?.code_siep} />
                                            </LoadingWrapper>
                                        </Col>
                                    </Row>
                                    <Row style={{ flex: 1, rowGap: '5px' }}>
                                        <Col md={8} className='element-2-md-8'>
                                            <LoadingWrapper isLoading={!establishment}>
                                                <Mortality dataColors='["--vz-primary", "--vz-secondary", "--vz-success"]' />
                                            </LoadingWrapper>
                                        </Col>
                                        <Col md={4} className='element-2-md-4'>
                                            <LoadingWrapper isLoading={!establishment}>
                                                <DissolvedOxygenSummary title={"Oxígeno Disuelto"} um={"mg/L"} code_siep={establishment?.code_siep} />
                                            </LoadingWrapper>
                                        </Col>
                                    </Row>
                                    <Row className='element-3-row' style={{ flex: 1, justifyContent: 'center' }}>
                                        <Col md={5} className='element-3-md-5'>
                                            <LoadingWrapper isLoading={!establishment}>
                                                <Fuel establishment_id={establishment?._id} dataColors='["--vz-primary", "--vz-danger", "--vz-secondary"]' />
                                            </LoadingWrapper>
                                        </Col>
                                        <Col md={3} className='element-3-md-3'>
                                            <LoadingWrapper isLoading={!establishment}>
                                                <FuelStorage id={id} />
                                            </LoadingWrapper>
                                        </Col>
                                        <Col md={4} className='element-3-md-4'>
                                            <LoadingWrapper isLoading={!establishment}>
                                                <TemperatureSummary title={"Temperatura"} um={"°C"} code_siep={establishment?.code_siep} />
                                            </LoadingWrapper>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Col>

                        <Col md={2} className='summary-dashboard-3' >
                            <Row>
                                <Col md={12}>
                                    <LoadingWrapper isLoading={!establishment}>
                                        <Captaincy nmcapitania_id={establishment?.nmcapitania_id} nmbahia_id={establishment?.nmbahia_id} />
                                    </LoadingWrapper>
                                </Col>
                            </Row>
                            <Row className='summary-date-card'>
                                <Col md={12}>
                                    <LoadingWrapper isLoading={!establishment}>
                                        <UpcomingSchedules id={id} />
                                    </LoadingWrapper>
                                </Col>

                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>

    );
};

export default EstablishmentSummary;
